import * as React from 'react';
import classNames from 'classnames';
import { useSegmentation } from '@/contexts/SegmentationContext';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import styles from './Price.module.scss';

export interface PriceProps {
	readonly className?: string;
	readonly price?: number;
	readonly size?: 'sm' | 'md' | 'lg';
	readonly inline?: boolean;
	readonly isLoading?: boolean;
	readonly isError?: boolean;
	readonly cultureCode?: string;
	readonly currencyName?: string;
}

const capitalize = (word: string): string => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word);

/**
 * Generic Price component, used for displaying price everywhere on the site
 */
export const Price: React.FunctionComponent<PriceProps> = ({
	className,
	price,
	size = 'md',
	inline,
	isLoading,
	isError,
}) => {
	const { segmentationId } = useSegmentation();
	const { data: settings } = useGetSettings(segmentationId);
	const { data: translations } = useGetTranslations(segmentationId);
	const cultureCode = settings?.data.cultureCode ?? '';
	const currencyName = settings?.data.currencySettings.currencyName ?? '';

	const numberFormatter = React.useMemo(
		() => new Intl.NumberFormat(cultureCode, { style: 'currency', currency: currencyName }),
		[cultureCode, currencyName],
	);

	if (!cultureCode || !currencyName) {
		console.error("Can't format prices because culture info is missing");
		return null;
	}

	return (
		<div
			className={classNames(
				styles.wrapper,
				size && styles[`size${capitalize(size)}`],
				{ [styles.isInline]: inline, [styles.isLoading]: isLoading },
				className,
			)}
		>
			<p className={styles.price}>
				{isLoading ? (
					<i>{translations?.data.shared.loading}</i>
				) : (
					<>
						{isError ? (
							<i>{translations?.data.shared.priceError}</i>
						) : (
							<>
								{price !== undefined && price >= 0 ? (
									numberFormatter.format(price)
								) : (
									<i>{translations?.data.shared.noPrice}</i>
								)}
							</>
						)}
					</>
				)}
			</p>
		</div>
	);
};
